import type { Product } from '@scayle/storefront-nuxt'
import {
  getLowestPrice,
  getPrice,
  getFirstAttributeValue,
  getTotalAppliedReductions,
} from '@scayle/storefront-nuxt'
import type {
  TrackViewItemListEventParams,
  TrackViewItemParams,
  TrackingEvent,
  TrackingPayload,
  ProductListData,
  TrackSelectItemEventParams,
} from '../types'
import useProductTrackingLocalStorage from '../useProductTrackingLocalStorage'
import { getCategoryLevels, getSizeKey } from '~/composables'
import { getSizeLabel } from '#layers/base/types/variant'

const collectProductListItems = (
  items: Product[],
  options: { listName: string; listId: string },
) => {
  return items.map(
    (product: Product, idx: number): ProductListData => ({
      product,
      list: {
        name: options.listName,
        id: options.listId,
        index: idx + 1,
      },
    }),
  )
}

const useProductEvents = (
  track: (event: TrackingEvent, payload: TrackingPayload) => any,
) => {
  const currentShop = useCurrentShop()
  const { currency } = currentShop.value ?? {}
  const { getStoredProductTrackingItem, storeProductTracking } =
    useProductTrackingLocalStorage()

  return {
    trackSelectItem: ({
      product,
      index,
      listName,
      listType,
    }: TrackSelectItemEventParams) => {
      const categoryLevels = getCategoryLevels(product.categories)
      const price = getLowestPrice(product?.variants ?? [])

      const ecommerceItem: EcommerceItem = {
        item_id: product.id.toString(),
        item_sku: product.referenceKey || '',
        item_name:
          getFirstAttributeValue(product.attributes, 'name')?.label || '',
        item_brand:
          getFirstAttributeValue(product.attributes, 'brand')?.label || '',
        item_variant:
          getFirstAttributeValue(product.attributes, 'color')?.label || '',
        item_size: DEFAULT_SIZE,
        item_size_key: DEFAULT_SIZE_KEY,
        item_category: categoryLevels.item_category || '',
        item_category2: categoryLevels.item_category2 || '',
        item_category3: categoryLevels.item_category3 || '',
        price: toFloat(price.withTax),
        discount: toFloat(getTotalAppliedReductions(price).absoluteWithTax),
        index: index + 1,
        coupon: '',
        quantity: DEFAULT_QUANTITY,
        stock: DEFAULT_STOCK,
        ordertype: 'shop',
        item_list_name: listName,
        item_list_type: listType,
      }

      track('select_item', {
        ecommerce: {
          items: [ecommerceItem],
        },
      })

      if (product.referenceKey) {
        storeProductTracking({
          [product.referenceKey]: {
            index: index + 1,
            master_key:
              product.masterKey != null ? String(product.masterKey) : undefined,
            item_category: categoryLevels.item_category,
            item_list_name: listName,
            item_list_type: listType,
          },
        })
      }
    },
    trackViewItemList: (params: TrackViewItemListEventParams) => {
      const {
        items,
        type,
        name,
        totalItemQuantity,
        viewItemQuantity,
        totalPages,
        currentPage,
        filters,
        filterOptions,
        sorting,
      } = params

      const ecommerceItems: EcommerceItem[] = items.map((product) => {
        const categoryLevels = getCategoryLevels(product.categories)
        const price = getLowestPrice(product?.variants ?? [])

        return {
          item_id: product.id.toString(),
          item_sku: product.referenceKey || '',
          item_name:
            getFirstAttributeValue(product.attributes, 'name')?.label || '',
          item_brand:
            getFirstAttributeValue(product.attributes, 'brand')?.label || '',
          item_variant:
            getFirstAttributeValue(product.attributes, 'color')?.label || '',
          item_size: DEFAULT_SIZE,
          item_size_key: DEFAULT_SIZE_KEY,
          item_category: categoryLevels.item_category || '',
          item_category2: categoryLevels.item_category2 || '',
          item_category3: categoryLevels.item_category3 || '',
          price: toFloat(price.withTax),
          discount: toFloat(getTotalAppliedReductions(price).absoluteWithTax),
          index: product.index + 1,
          coupon: '',
          quantity: DEFAULT_QUANTITY,
          stock: DEFAULT_STOCK,
          ordertype: 'shop',
          item_list_name: name,
          item_list_type: type,
        }
      })

      return track('view_item_list', {
        ecommerce: {
          item_list_type: type,
          item_list_name: name,
          item_list_total_item_quantity: totalItemQuantity,
          item_list_view_item_quantity: viewItemQuantity,
          item_list_total_pages: totalPages,
          item_list_current_page: currentPage,
          item_list_filter: filters,
          item_list_filter_options: filterOptions,
          item_list_sorting: sorting,
          items: ecommerceItems,
        },
      })
    },

    trackViewItem: ({
      product,
      // TODO-SST: it seems that this variant should not be optional,
      // and that it always defaults to the first variant if no active variant
      variant,
      index = 0,
      listName,
    }: TrackViewItemParams) => {
      const categoryLevels = getCategoryLevels(product.categories)
      const storedProductTracking = getStoredProductTrackingItem(
        String(product.referenceKey),
      )

      // Check if user came from search
      const isSearchResult = import.meta.client
        ? sessionStorage.getItem('search_result') === 'true'
        : false
      if (isSearchResult && import.meta.client) {
        sessionStorage.removeItem('search_result')
      }

      const price = variant
        ? getPrice(variant)
        : getLowestPrice(product.variants || [])

      const items: EcommerceItem[] = [
        {
          item_id: product.id.toString(),
          item_sku: variant?.referenceKey || product.referenceKey || '',
          item_name:
            getFirstAttributeValue(product.attributes, 'name')?.label || '',
          item_brand:
            getFirstAttributeValue(product.attributes, 'brand')?.label || '',
          item_variant:
            getFirstAttributeValue(product.attributes, 'color')?.label || '',
          item_size: variant ? getSizeLabel(variant) : DEFAULT_SIZE,
          item_size_key: variant?.referenceKey
            ? getSizeKey(variant.referenceKey) || ''
            : DEFAULT_SIZE_KEY,
          item_category: categoryLevels.item_category || '',
          item_category2: categoryLevels.item_category2 || '',
          item_category3: categoryLevels.item_category3 || '',
          price: toFloat(price.withTax),
          discount: toFloat(
            getTotalAppliedReductions(price).absoluteWithTax || 0,
          ),
          index: index + 1,
          coupon: '',
          quantity: DEFAULT_QUANTITY,
          stock: variant?.stock.quantity || DEFAULT_STOCK,
          ordertype: 'shop',
          item_list_name:
            listName || storedProductTracking?.item_list_name || '',
          item_list_type: storedProductTracking?.item_list_type || '',
        },
      ]

      track('view_item', {
        ...(isSearchResult && { search_result: true }),
        ecommerce: {
          currency,
          value: toFloat(price.withTax),
          net_value: toFloat(price.withoutTax),
          items,
        },
      })
    },
    collectProductListItems,
  }
}

export default useProductEvents
